// Question View - centered question screen layout with answers
// Receives all data from ViewModel via props, no business logic

import { Dispatchers } from '@client/new/helpers/types';
import { PlaybookLogo } from '@design-system/components/logo/PlaybookLogo';
import { QuestionVM } from '../view-model/Question.viewModel';
import { OnboardingVMActionMap } from '../view-model/Onboarding.viewModel';
import { ProgressBar } from './components/ProgressBar';
import { AnswerOption } from './components/AnswerOption';
import * as styles from './Question.view.css';

export type QuestionViewProps = {
  question: QuestionVM;
  currentStep: number;
  totalSteps: number;
  canGoBack: boolean;
  isSaving: boolean;
} & Dispatchers<OnboardingVMActionMap>;

const BackButton = ({
  onClick,
  className,
}: {
  onClick: () => void;
  className: string;
}) => (
  <button
    type="button"
    className={className}
    onClick={onClick}
    data-testid="back-button"
    aria-label="Go back"
  >
    <span className={styles.backIcon}>←</span>
  </button>
);

const SkipButton = ({
  onClick,
  disabled,
  className,
}: {
  onClick: () => void;
  disabled: boolean;
  className: string;
}) => (
  <button
    type="button"
    className={className}
    onClick={onClick}
    disabled={disabled}
    data-testid="skip-button"
  >
    Skip for now
  </button>
);

const AnswerList = ({
  question,
  dispatch,
}: {
  question: QuestionVM;
  dispatch: Dispatchers<OnboardingVMActionMap>['dispatch'];
}) => {
  const isMultiChoice = question.type === 'MULTI_CHOICE';
  return (
    <>
      {question.answers.map((answer, index) => (
        <div
          key={answer.id}
          className={styles.answerItem}
          style={{ animationDelay: `${index * 0.05}s` }}
        >
          <AnswerOption
            id={answer.id}
            title={answer.title}
            subtitle={answer.subtitle}
            isSelected={answer.isSelected}
            onClick={() =>
              dispatch(isMultiChoice ? 'ToggleAnswer' : 'SelectAnswer', {
                questionId: question.id,
                answerId: answer.id,
              })
            }
          />
        </div>
      ))}
    </>
  );
};

const ContinueButton = ({
  onClick,
  disabled,
  className,
}: {
  onClick: () => void;
  disabled: boolean;
  className: string;
}) => (
  <button
    type="button"
    className={className}
    onClick={onClick}
    disabled={disabled}
    data-testid="continue-button"
  >
    Continue
  </button>
);

export const QuestionView = ({
  question,
  currentStep,
  totalSteps,
  canGoBack,
  isSaving,
  dispatch,
  lazyDispatch,
}: QuestionViewProps) => {
  const isFirstStep = currentStep === 1;
  const isMultiChoice = question.type === 'MULTI_CHOICE';

  return (
    <div className={styles.screen} data-testid="question-view">
      <header className={styles.header}>
        {canGoBack && (
          <BackButton
            onClick={lazyDispatch('GoBack')}
            className={styles.backButton}
          />
        )}
        <PlaybookLogo showGlyph showName height={24} color="N-100" />
      </header>

      <div className={styles.card}>
        {isFirstStep ? (
          <>
            <span className={styles.introBadge}>
              Create your thriving fitness app
            </span>

            <p className={styles.introDescription}>
              <strong>
                Get started quickly by answering just a few questions.
              </strong>
              <br />
              We&apos;ll help tailor your first product to your needs, helping
              you to start making money as fast as possible.
            </p>
          </>
        ) : (
          <div className={styles.progressSection}>
            <ProgressBar current={currentStep} total={totalSteps} />
          </div>
        )}

        <h1 className={styles.questionTitle}>{question.title}</h1>

        {!isFirstStep && question.subtitle && (
          <p className={styles.questionSubtitle}>{question.subtitle}</p>
        )}

        <div className={styles.answersContainer} key={question.id}>
          <AnswerList question={question} dispatch={dispatch} />
        </div>

        {isMultiChoice && (
          <ContinueButton
            onClick={lazyDispatch('GoNext')}
            disabled={!question.canProceed || isSaving}
            className={styles.continueButton}
          />
        )}

        <SkipButton
          onClick={lazyDispatch('SkipToStrategy')}
          disabled={isSaving}
          className={styles.skipButton}
        />
      </div>
    </div>
  );
};
